import * as React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Moment from 'react-moment'
import * as CurrencyFormat from 'react-currency-format'
import { RouteComponentProps, withRouter } from 'react-router'
import { Input, Table, Button, Collapse, FormGroup } from 'reactstrap'

import { RootState } from '../../store/rootReducer'
import { EmployeeUpdateRequest } from '../../lib/api/employees'
import { Employee } from '../../lib/interfaces/Employee.interface'
import { Location } from '../../lib/interfaces/Location.interface'
import { EmployeeComment } from '../../lib/interfaces/EmployeeComment.interface'
import { Termination, TerminationReason } from '../../lib/interfaces/Termination.interface'
import { User, hasPermission, hasAnyPermission } from '../../lib/interfaces/User.interface'
import { EmploymentStatus, employmentStatusToString } from '../../lib/interfaces/EmploymentStatus.interface'

import Breadcrumbs from '../Breadcrumbs.component'
import FileList from '../../common/FileList.component'
import FileDropzone from '../../common/FileDropzone.component'
import LocationImage from '../../common/LocationImage.component'
import ChangeableBadge from 'src/common/ChangeableBadge.component'
import EntityCommentList from 'src/common/EntityCommentList.component'
import ClaimsList, { ClaimsListViewMode, ClaimsListViewModeToggler } from 'src/common/ClaimsList.component'

import EmployeeEditionModal from './components/EmployeeEdition.modal'
import TerminationFlowModal from '../terminations/termination-flow/components/TerminationFlow.modal'

import terminationReasons from 'src/utils/termination-reason'

import { formatName } from 'src/lib/helpers/format'
import withConfirm, { WithConfirmProps } from 'src/lib/helpers/withConfirm'

import * as employeesActions from '../../store/employees/actions'

const moment = require('moment')

const get = require('lodash/get')
const map = require('lodash/map')
const last = require('lodash/last')
const size = require('lodash/size')
const sortBy = require('lodash/sortBy')
const indexOf = require('lodash/indexOf')
const isEmpty = require('lodash/isEmpty')
const compact = require('lodash/compact')
const includes = require('lodash/includes')

interface RouteInfo {
  employeeId: string
}

export interface EmployeeDetailsProps extends WithConfirmProps, RouteComponentProps<RouteInfo> {
  user: User
  employee?: Employee
  fetchFilesError?: any
  isFetchingFiles: boolean
  isUploadingFile: boolean
  isPostingComment: boolean
  isCreatingTermination: boolean
  resetCurrentEmployee: () => void
  fetchEmployeeFiles: (employeeId: string) => void
  fetchEmployeeComments: (employeeId: string) => void
  postEmployeeComment: (employeeId: string, comment: string) => void
  uploadEmployeeFile: (employeeId: string, file: any, confidential?: boolean) => void
  fetchEmployee: (employeeId: string, next?: (error?: any, employee?: Employee) => void) => void
  updateEmployee: (employeeId: string, employee: EmployeeUpdateRequest, next?: (err?: any, employee?: Employee) => void) => void
}

export interface EmployeeDetailsState {
  comment: string,
  expandedClaims: number[]
  terminationModalOpen: boolean
  employeeEditionModalOpen: boolean
  claimsListViewMode: ClaimsListViewMode
}

class EmployeeDetails extends React.PureComponent<EmployeeDetailsProps, EmployeeDetailsState> {
  state: EmployeeDetailsState = {
    comment: '',
    expandedClaims: [],
    claimsListViewMode: 'table',
    terminationModalOpen: false,
    employeeEditionModalOpen: false
  }

  get employeeId() {
    return this.props.match.params.employeeId
  }

  componentDidMount() {
    const { fetchEmployee, fetchEmployeeComments, resetCurrentEmployee } = this.props

    resetCurrentEmployee()
    fetchEmployee(this.employeeId, (error?: any, employee?: Employee) => {
      if (!error) {
        this.fetchFiles()
        fetchEmployeeComments(this.employeeId)
      }
    })
  }

  fetchFiles = () => {
    this.props.fetchEmployeeFiles(this.employeeId)
  }

  handleToggleExpandClaim(claimId) {
    const { expandedClaims } = this.state

    let newExpandedClaims: number[] = []
    if (includes(expandedClaims, claimId)) {
      const claimIndex = indexOf(expandedClaims, claimId)
      newExpandedClaims = [ ...expandedClaims ]
      newExpandedClaims.splice(claimIndex, 1)
    }
    else {
      newExpandedClaims = [ ...expandedClaims, claimId ]
    }

    this.setState({ expandedClaims: newExpandedClaims })
  }

  handleFileUpload = (file, confidential?: boolean) => {
    const { uploadEmployeeFile } = this.props
    uploadEmployeeFile(this.employeeId, file, confidential)
  }

  handleCommentSubmit = e => {
    e.preventDefault()

    const { comment } = this.state
    const { postEmployeeComment } = this.props

    this.setState(
      { comment: '' },
      () => postEmployeeComment(this.employeeId, comment)
    )
  }

  get latestTermination(): Termination|undefined {
    const { employee } = this.props

    if (employee && size(employee.terminations) > 0) {
      return last(sortBy(employee.terminations, 'lastDayOfWork'))
    }

    return undefined
  }

  getTerminationFormattedReason(reason: TerminationReason) {
    return terminationReasons[reason]
  }

  get latestTerminationFormattedReason(): string|undefined {
    const latestTermination = this.latestTermination
    if (latestTermination && latestTermination.reason) {
      return this.getTerminationFormattedReason(latestTermination.reason) || '-'
    }

    return '-'
  }

  get latestTerminationFormattedDate(): string|undefined {
    const latestTermination = this.latestTermination
    if (latestTermination) {
      return moment(latestTermination.lastDayOfWork).format('MMMM DD[,] YYYY')
    }

    return '-'
  }

  handleSetEmployeeLocation(location: Location) {
    this.props.updateEmployee(this.employeeId, { locationId: location.id })
  }

  handleSetEmploymentStatus(employmentStatus: EmploymentStatus) {
    this.props.updateEmployee(this.employeeId, { employmentStatus })
  }

  renderEmployeeName(employee?: Employee) {
    return formatName(employee)
  }

  render() {
    const { claimsListViewMode, terminationModalOpen, employeeEditionModalOpen } = this.state
    const {
      user,
      employee,
      fetchFilesError,
      isFetchingFiles,
      isUploadingFile,
      isPostingComment,
      isCreatingTermination
    } = this.props

    const isAdmin = hasPermission(user, 'ROLE_ADMIN')

    return (
      <div>
        <Breadcrumbs breadcrumbs={[
          { name: 'Employees', link: '/employees' },
          { name: this.renderEmployeeName(employee) || 'Unnamed Employee', active: true }
        ]} />

        {employee &&
          <div className="accident-page mt-3">
            <EmployeeEditionModal
              employee={employee}
              open={employeeEditionModalOpen}
              onClose={() => this.setState({ employeeEditionModalOpen: false })}
            />

            <TerminationFlowModal
              employee={employee}
              open={terminationModalOpen}
              onClose={() => this.setState({ terminationModalOpen: false })}
            />

            <section className="container-fluid pl-0 pr-0 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">{this.renderEmployeeName(employee) || 'Unnamed Employee'}</h5>
                {hasAnyPermission(user, ['ROLE_APPLICATION_ADMIN', 'ROLE_EMPLOYEE_EDITING']) && (
                  <Button
                    size="sm"
                    color="light"
                    className="ggbutton ml-3"
                    onClick={() => this.setState({ employeeEditionModalOpen: true })}
                  >
                    Edit
                  </Button>
                )}

                {!get(employee, 'terminations.length') && hasAnyPermission(user, ['ROLE_OFFICE_TERMINATE', 'ROLE_LOCATION_TERMINATE']) && (
                  <Button
                    size="sm"
                    color="dark"
                    className="ggbutton ml-3"
                    disabled={isCreatingTermination}
                    onClick={() => this.setState({ terminationModalOpen: true })}
                  >
                    {isCreatingTermination &&
                      <FontAwesomeIcon
                        spin
                        className="mr-3"
                        icon={['fas', 'spinner']}
                      />
                    }
                    Terminate
                  </Button>
                )}
              </div>

              <div className="row no-gutters mt-4">
                <div className="col-12 col-md-8 col-lg-5 pr-md-3">
                  <div className="ggcard p-3">
                    <table>
                      <tbody>
                        <tr>
                          <td className="pr-3">Employee ID:</td>
                          <td>{employee.id}</td>
                        </tr>
                        <tr>
                          <td className="pr-3">Name:</td>
                          <td>{this.renderEmployeeName(employee)}</td>
                        </tr>
                        <tr>
                          <td className="pr-3">Hire date:</td>
                          <td>{employee.hireDate ? moment(employee.hireDate).format('MM/DD/YYYY') : '-'}</td>
                        </tr>
                        <tr>
                          <td className="pr-3">Status:</td>
                          <td>
                            <ChangeableBadge
                              enableChange
                              containerMinWidth="90px"
                              style={{ minWidth: '70px' }}
                              value={employee.employmentStatus}
                              getOptionValue={status => status}
                              badgeText={employee.employmentStatus || '-'}
                              options={Object.keys(EmploymentStatus)}
                              changePermissions={['ROLE_APPLICATION_ADMIN']}
                              getOptionLabel={status => employmentStatusToString(status)}
                              onChange={(status: EmploymentStatus) => {
                                this.props.confirm(
                                  () => this.handleSetEmploymentStatus(status),
                                  {
                                    description:
                                      <span>
                                        Are you sure you want to change <b>{this.renderEmployeeName(employee)}</b>'s employment status to <b>{employmentStatusToString(status)}</b>?
                                      </span>
                                  })
                              }}
                            />
                          </td>
                        </tr>
                        {employee.employmentStatus === EmploymentStatus.TERMINATED &&
                          <React.Fragment>
                            <tr>
                              <td className="pr-3">Last day of work:</td>
                              <td>{this.latestTerminationFormattedDate}</td>
                            </tr>
                            <tr>
                              <td className="pr-3">Termination reason:</td>
                              <td>{this.latestTerminationFormattedReason}</td>
                            </tr>
                          </React.Fragment>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>

                {employee.location && (
                  <div className="col-12 col-md-4 col-lg-3 pr-lg-3 mt-3 mt-md-0">
                    <div className="ggcard h-100" style={{ minHeight: '125px' }}>
                      <LocationImage
                        enableChange
                        locationCode={employee.location.code}
                        changePermissions={['ROLE_APPLICATION_ADMIN']}
                        onChange={(location: Location) => {
                          this.props.confirm(
                            () => this.handleSetEmployeeLocation(location),
                            {
                              description:
                                <span>
                                  Are you sure you want to change <b>{this.renderEmployeeName(employee)}</b>'s location to <b>{location.name}</b>?
                                </span>
                            })
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="container-fluid pl-0 pr-0 mt-5 ml-0">
              <h5 className="title">Employee Comments</h5>

              <div className="w-100 mt-3">
                <div className="d-flex flex-column">
                  <EntityCommentList comments={employee.comments} />

                  <div className="ggcard p-3 mt-3">
                    <span className="text-uppercase"><b>Add an update:</b></span>
                    <FormGroup className="mt-3 mb-0">
                      <Input
                        type="textarea"
                        value={this.state.comment}
                        onChange={e => this.setState({ comment: e.target.value })}
                      />

                      <Button
                        type="submit"
                        color="primary"
                        className="btn w-100 mt-4"
                        onClick={this.handleCommentSubmit}
                        disabled={isPostingComment || isEmpty(this.state.comment)}
                      >
                        {isPostingComment &&
                          <FontAwesomeIcon
                            spin
                            className="mr-3"
                            icon={['fas', 'spinner']}
                          />
                        }
                        Submit
                      </Button>
                    </FormGroup>
                  </div>
                </div>
              </div>
            </section>

            <section className="pl-0 mt-5 mb-4 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">Claims Involved In</h5>
                <ClaimsListViewModeToggler
                  className="ml-2"
                  viewMode={claimsListViewMode}
                  onChange={viewMode => this.setState({ claimsListViewMode: viewMode })}
                />
              </div>

              <ClaimsList
                className="mt-2"
                showDetailsOnClick
                claims={employee.claims}
                viewMode={claimsListViewMode}
                emptyMessage="There are no claims attached to this accident."
                onRowClick={() => this.setState({ claimsListViewMode: 'card' })}
                columns={[
                  'erac',
                  'vendor',
                  'vin',
                  'vehicle',
                  'status',
                  'claim-type',
                  'cost'
                ]}
              />
            </section>

            {employee.terminations && employee.terminations.length > 0 && (
              <section className="pl-0 mt-5 mb-4 ml-0">
                <div className="d-flex align-items-center mb-2">
                  <h5 className="title mb-0">Terminations</h5>
                </div>

                <div className="row no-gutters mt-4">
                  <Table hover className="ggtable">
                    <thead>
                      <tr>
                        <th>Date created</th>
                        <th>Confirmed</th>
                        <th>Reason</th>
                        <th>Voluntary</th>
                        <th>Re-eligible</th>
                      </tr>
                    </thead>
                    
                    <tbody>
                      {map(employee.terminations, termination => (
                        <tr key={termination.id} onClick={() => this.props.history.push(`/terminations/${termination.id}`)}>
                          <td><Moment date={termination.dateCreated} format="MM.DD.YYYY" /></td>
                          <td>{termination.confirmed ? 'Yes' : 'No'}</td>
                          <td>{this.getTerminationFormattedReason(termination.reason)}</td>
                          <td>{termination.voluntary ? 'Yes' : 'No'}</td>
                          <td>{termination.eligibleForRehire ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </section>
            )}

            <section className="pl-0 mt-5 mb-4 ml-0">
              <div className="d-flex align-items-center mb-1">
                <h5 className="title mb-0">Files Attached to This Employee</h5>
                {isFetchingFiles && <FontAwesomeIcon spin className="ml-2" icon={['fas', 'spinner']} style={{ fontSize: '20px' }} />}
              </div>

              <div className="claimscontainer mt-4">
                <FileList
                  entity="employee"
                  files={employee.files}
                  entityId={employee.id}
                  error={fetchFilesError}
                  onDelete={this.fetchFiles}
                  className="row no-gutters"
                  onTryAgain={this.fetchFiles}
                  fileWrapperClassName="col-4 pr-3 pb-3"
                  canDelete={hasAnyPermission(user, ['ROLE_APPLICATION_ADMIN'])}
                />

                <div className="d-flex align-items-center mt-4">
                  <div className="pr-2" style={{ width: isAdmin ? '75%' : '100%' }}>
                    <FileDropzone
                      isLoading={isUploadingFile}
                      onDrop={this.handleFileUpload}
                    />
                  </div>

                  {isAdmin && (
                    <div className="pl-2" style={{ width: '25%' }}>
                      <FileDropzone
                        title="Confidential files"
                        isLoading={isUploadingFile}
                        onDrop={file => this.handleFileUpload(file, true)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        }
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  employee: state.employees.employeeDetails,
  fetchFilesError: state.employees.fetchFilesError,
  isFetchingFiles: state.employees.isFetchingFiles,
  isUploadingFile: state.employees.isUploadingFile,
  isPostingComment: state.employees.isPostingComment,
  isCreatingTermination: state.terminations.isCreating
})

const mapDispatchToProps = (dispatch) => ({
  resetCurrentEmployee: () => dispatch(employeesActions.setCurrentEmployee(undefined)),
  fetchEmployeeFiles: (employeeId: string) => dispatch(employeesActions.fetchEmployeeFiles(employeeId)),
  fetchEmployeeComments: (employeeId: string) => dispatch(employeesActions.fetchEmployeeComments(employeeId)),
  postEmployeeComment: (employeeId: string, comment: string) =>
  dispatch(employeesActions.postEmployeeComment(employeeId, comment)),
  uploadEmployeeFile: (employeeId: string, file: any, confidential?: boolean) =>
    dispatch(employeesActions.uploadEmployeeFile(employeeId, file, confidential)),
  fetchEmployee: (employeeId: string, next?: (error?: any, employee?: Employee) => void) =>
    dispatch(employeesActions.fetchEmployee(employeeId, next)),
  updateEmployee: (employeeId: string, employee: EmployeeUpdateRequest, next?: (err?: any, employee?: Employee) => void) =>
    dispatch(employeesActions.updateEmployee(employeeId, employee, next))
})

export default withConfirm(withRouter(connect(mapStateToProps, mapDispatchToProps)(EmployeeDetails)))



// WEBPACK FOOTER //
// ./src/routes/employees/EmployeeDetails.page.tsx